import { LegacyRef, useCallback, useEffect, useRef, useState } from "react";
import AutoComplete from "./Helpers/Autocomplete";
import FormElementWrapper from "./Helpers/FormElementWrapper";
import { IOption } from "./Helpers/FormOption";
import intlTelInput from "intl-tel-input";

import "intl-tel-input/build/css/intlTelInput.min.css";

export interface IFormComponent {
  value?: string | Date;
  default?: string | number;
  onChange?: (newVal: string) => void;
  small?: boolean;
  placeholder: any;
  options?: IOption[]; //INCLUDE THIS IF INPUT TYPE IS SELECT OR AUTOCOMPLETE
  onDelete?: (val: string | number) => Promise<void>;
  onAddOption?: () => void;
  inputRef?: LegacyRef<HTMLInputElement> | null;
  icon?:
    | "mail"
    | "book"
    | "puzzle"
    | "user"
    | "globe"
    | "city"
    | "calendar"
    | "badge"
    | "timer";
}

export interface IInput extends IFormComponent {
  type:
    | "date"
    | "email"
    | "number"
    | "password"
    | "text"
    | "select"
    | "autocomplete"
    | "passcode"
    | "tel";
}

//TODO: Debounce onChange function
export default function Input(props: IInput) {
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [openDropDown, setOpenDropDown] = useState(false);
  const [value, setValue] = useState("");
  const defaultRef = useRef<HTMLInputElement>(null);

  const onChange = (e: any) => {
    const currentInput = e.target.value;
    if (props.type === "autocomplete" && props.options) {
      setActiveOptionIndex(0);
      setOpenDropDown(true);
    }
    handleSetValue(currentInput);
  };

  useEffect(() => {
    const filteredOptions = (props.options ?? []).filter(
      (option: IOption) =>
        option.label.toLowerCase().indexOf(value.toLowerCase()) > -1 // FILTER CONDITION
    );
    setFilteredOptions(filteredOptions);
  }, [props.options, value]);

  const onClickOption = (e: any) => {
    setFilteredOptions([]);
    handleSetValue(e.target.innerText);
    setActiveOptionIndex(0);
    setOpenDropDown(false);
  };

  const handleSetValue = (newVal: string) => {
    props.onChange?.(newVal);
    setValue(newVal);
  };

  //for date input
  const handleFocusForDateInputType = useCallback(() => {
    if (defaultRef.current?.type && props.value === undefined) {
      defaultRef.current.type = "date";
    }
  }, [defaultRef, props.value]);

  const handleBlurForDateInputType = useCallback(() => {
    if (defaultRef.current?.type && props.value === undefined) {
      defaultRef.current.type = "text";
    }
  }, [defaultRef, props.value]);

  return (
    <FormElementWrapper icon={props.icon}>
      <input
        onClick={
          //@ts-ignore
          props.type === "date" ? (e) => e.target.showPicker() : undefined
        }
        value={props.type === "date" ? undefined : (props.value as string)}
        defaultValue={props.default}
        type={
          props.type === "passcode"
            ? "text"
            : props.type === "date" && props.value === undefined
            ? "text"
            : props.type
        }
        placeholder={props.placeholder}
        inputMode={
          props.type === "text" || props.type === "autocomplete"
            ? "text"
            : props.type === "date"
            ? "numeric"
            : undefined
        }
        pattern="\d*"
        onChange={onChange}
        maxLength={props.type === "passcode" ? 4 : 524288}
        autoComplete={props.type === "tel" ? "tel" : undefined}
        onMouseEnter={
          props.type === "date" ? handleFocusForDateInputType : undefined
        }
        onBlur={props.type === "date" ? handleBlurForDateInputType : undefined}
        className={`
        text-[16px] 
        placeholder:text-brij-DarkGrey 
        focus:outline-transparent 
        focus:border-0 
        border-0 grow 
        overflow-hidden 
        text-ellipsis 
        bg-transparent 
        focus:ring-transparent
        autofill:bg-yellow-200
        p-0
        `}
        ref={props.inputRef ?? defaultRef}
      />
      {props.type === "autocomplete" &&
        props.options &&
        openDropDown &&
        value &&
        !(
          filteredOptions.length === 1 && filteredOptions[0].value === value
        ) && (
          <AutoComplete
            placeholder={props.placeholder}
            filteredOptions={filteredOptions}
            activeOptionIndex={activeOptionIndex}
            onClickOption={onClickOption}
          />
        )}
    </FormElementWrapper>
  );
}
